import { useAuth } from '@/hooks/use-auth'
import { todayIso, dayIso, addDaysIso, isoToDate, startOfWeekIso } from '@/lib/today'
import { useTimesheet, entryMinutes, fmtHours, type TimeEntry } from '@/hooks/use-time'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Printer, Download, Loader2, AlertTriangle, Plus, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useTeam, useCompany } from '@/hooks/use-company'
import { useCostingRows } from '@/hooks/use-costing'
import { cn } from '@/lib/utils'
import { Page, PageHeader, Card, CardHeader, KpiCard, TabPills, DetailSkeleton, EmptyState, SkeletonRows, btnPrimary, btnSecondary, btnGhost, hoverActions, useConfirm } from '@/components/rm'
import { Select } from '@/components/rm/form'
import { fetchPayrollExport, toCsv, downloadCsv } from '@/lib/payroll-export'
import { sourceBreakdown } from '@/lib/marketing-sources'
import { useReportTemplates, useReportMutations, rangeLabel } from '@/hooks/use-reports'
import { toast } from 'sonner'
import { phFormat, peso, phDay, manilaDayIso, manilaWallToIso } from '@/lib/ph'
import { FEATURES } from '@/lib/features'

export const Route = createFileRoute('/_app/reports')({ component: ReportsPage })

const money = (n: number) => peso(n, { whole: true })
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function ReportsPage() {
  const { role } = useAuth()
  const isAdmin = role === 'company-admin'
  const [tab, setTab] = useState('runsheet')
  return (
    <Page>
      <div className="print:hidden">
        <PageHeader title="Reports" subtitle="Run sheets, timesheets and operational performance." />
      </div>
      <div className="mb-5 mt-5 print:hidden">
        <TabPills active={tab} onChange={setTab} chips={[{ id: 'runsheet', label: 'Job run sheet' }, { id: 'timesheets', label: 'Timesheets' }, ...(isAdmin ? [{ id: 'overview', label: 'Overview' }, { id: 'profit', label: 'Profitability' }, { id: 'marketing', label: 'Marketing' }, { id: 'custom', label: 'Custom' }] : [])]} />
      </div>
      {tab === 'custom' && isAdmin ? <CustomReports /> : tab === 'overview' && isAdmin ? <OverviewReport /> : tab === 'profit' && isAdmin ? <ProfitReport /> : tab === 'marketing' && isAdmin ? <MarketingReport /> : tab === 'timesheets' ? <TimesheetReport /> : <JobRunSheet />}
    </Page>
  )
}

interface ReportData {
  revenue90: number
  revenuePrev90: number
  outstanding: number
  pipeline: number
  pipelineWeighted: number
  openQuotes: number
  avgJobValue: number
  paidCount: number
  winRate: number
  revenueByMonth: { label: string; value: number }[]
  accepted: number
  total: number
}

function OverviewReport() {
  const q = useQuery({
    queryKey: ['reports'],
    queryFn: async (): Promise<ReportData> => {
      const since = new Date(); since.setDate(since.getDate() - 90)
      const since180 = new Date(); since180.setDate(since180.getDate() - 180)
      const [paid, quotes] = await Promise.all([
        supabase.from('customer_invoice').select('total, amount_paid, paid_at, issue_date, status').is('deleted_at', null),
        supabase.from('quote').select('status, total, valid_until').is('deleted_at', null),
      ])
      const inv = (paid.data ?? []) as Array<{ total: number; amount_paid: number; paid_at: string | null; issue_date: string | null; status: string }>
      const paidInv = inv.filter((i) => i.status === 'paid')
      const revenue90 = paidInv.filter((i) => new Date(i.paid_at ?? i.issue_date ?? 0) >= since).reduce((s, i) => s + Number(i.amount_paid ?? i.total ?? 0), 0)
      const revenuePrev90 = paidInv.filter((i) => { const d = new Date(i.paid_at ?? i.issue_date ?? 0); return d >= since180 && d < since }).reduce((s, i) => s + Number(i.amount_paid ?? i.total ?? 0), 0)
      const outstanding = inv.filter((i) => i.status !== 'paid' && i.status !== 'draft' && i.status !== 'void').reduce((s, i) => s + Math.max(0, Number(i.total ?? 0) - Number(i.amount_paid ?? 0)), 0)
      const avgJobValue = paidInv.length ? paidInv.reduce((s, i) => s + Number(i.total ?? 0), 0) / paidInv.length : 0

      // Revenue by month (last 5 months)
      const now = new Date()
      const buckets: { label: string; value: number }[] = []
      for (let k = 4; k >= 0; k--) {
        const d = new Date(now.getFullYear(), now.getMonth() - k, 1)
        const val = paidInv.filter((i) => { const p = new Date(i.paid_at ?? i.issue_date ?? 0); return p.getMonth() === d.getMonth() && p.getFullYear() === d.getFullYear() }).reduce((s, i) => s + Number(i.amount_paid ?? i.total ?? 0), 0)
        buckets.push({ label: MONTHS[d.getMonth()], value: val })
      }

      const qs = (quotes.data ?? []) as Array<{ status: string; total: number; valid_until: string | null }>
      const decided = qs.filter((x) => ['accepted', 'declined'].includes(x.status))
      const accepted = qs.filter((x) => x.status === 'accepted').length
      const winRate = decided.length ? Math.round((accepted / decided.length) * 100) : 0

      // 014 T007 — pipeline. Only quotes that are actually still live: sent, not decided, not
      // expired. A quote whose validity ran out last month is not money you can expect.
      const today = todayIso()
      const open = qs.filter((x) => x.status === 'sent' && (!x.valid_until || x.valid_until >= today))
      const pipeline = open.reduce((s2, x) => s2 + Number(x.total ?? 0), 0)
      // Weighted by the win rate the page already computes, so the two numbers cannot disagree.
      const pipelineWeighted = Math.round(pipeline * (winRate / 100))

      return { revenue90, revenuePrev90, outstanding, avgJobValue, paidCount: paidInv.length, winRate, revenueByMonth: buckets, accepted, total: decided.length || qs.length, pipeline, pipelineWeighted, openQuotes: open.length }
    },
  })

  const d = q.data
  const maxRev = Math.max(1, ...(d?.revenueByMonth.map((b) => b.value) ?? [1]))
  // Real period-on-period change — only shown when there is a previous period to compare against.
  const revDelta = d && d.revenuePrev90 > 0 ? Math.round(((d.revenue90 - d.revenuePrev90) / d.revenuePrev90) * 100) : null

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
        <KpiCard label="Revenue · 90 days" value={d ? money(d.revenue90) : '—'}
          delta={revDelta === null ? <span className="text-token-secondary">vs prior 90 days: no data</span>
            : <span className={revDelta >= 0 ? 'text-token-success' : 'text-token-danger'}>{revDelta >= 0 ? '▲' : '▼'} {Math.abs(revDelta)}% vs prior 90 days</span>} />
        <KpiCard label="Avg job value" value={d ? money(d.avgJobValue) : '—'} delta={<span className="text-token-secondary">across {d?.paidCount ?? 0} paid invoice{(d?.paidCount ?? 0) === 1 ? '' : 's'}</span>} />
        <KpiCard label="Quote win rate" value={d ? `${d.winRate}%` : '—'} delta={<span className="text-token-secondary">{d?.accepted ?? 0} of {d?.total ?? 0} decided quotes</span>} />
        <KpiCard label="Outstanding" value={d ? money(d.outstanding) : '—'} tone={d && d.outstanding > 0 ? 'orange' : 'ink'} delta={<span className="text-token-secondary">unpaid across issued invoices</span>} />
        <KpiCard label="Pipeline" value={d ? money(d.pipeline) : '—'}
          delta={<span className="text-token-secondary">{d?.openQuotes ?? 0} open quote{(d?.openQuotes ?? 0) === 1 ? '' : 's'} · {d ? money(d.pipelineWeighted) : '—'} at your win rate</span>} />
      </div>

      <AgingCard />

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        {/* Revenue by month — bar chart */}
        <Card className="p-5">
          <p className="font-display mb-5 text-[15px] font-bold text-token-primary">Revenue by month</p>
          <div className="flex h-[200px] items-end gap-5 px-2">
            {(d?.revenueByMonth ?? []).map((b, i, arr) => {
              const h = Math.max(6, Math.round((b.value / maxRev) * 170))
              const recent = i >= arr.length - 2
              return (
                <div key={i} className="flex flex-1 flex-col items-center gap-2">
                  <div className="flex w-full flex-1 items-end">
                    <div className="w-full rounded-t-[8px] transition-all" style={{ height: h, background: i === arr.length - 1 ? 'var(--accent)' : recent ? 'var(--text-muted)' : 'var(--canvas-subtle)' }} title={money(b.value)} />
                  </div>
                  <span className="text-[12.5px] text-token-secondary">{b.label}</span>
                </div>
              )
            })}
          </div>
        </Card>

        {/* Quote win rate — donut */}
        <Card className="p-5">
          <p className="font-display mb-5 text-[15px] font-bold text-token-primary">Quote win rate</p>
          <div className="flex flex-col items-center gap-6 sm:flex-row">
            <Donut pct={d?.winRate ?? 0} />
            <div className="space-y-2.5 text-[13px]">
              <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-brand" /> Accepted · {d?.winRate ?? 0}%</p>
              <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-canvas-subtle" /> Declined / open · {100 - (d?.winRate ?? 0)}%</p>
              <p className="pt-1 text-[12px] text-token-secondary">Across {d?.total ?? 0} decided quotes.</p>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}

/**
 * 014 T001-T003 — aging receivables.
 *
 * The buckets come from the `invoice_aging` RPC rather than from a client-side pass over the
 * invoice list, because "days past due" has to be computed in the company's timezone and this
 * database runs in UTC. They sum to the Outstanding figure in the row above; that is the spec's
 * acceptance test and it is checked against every tenant.
 *
 * Each bucket links into the invoice list already filtered, because a number nobody can act on is
 * just decoration.
 */
const BUCKETS: Array<{ id: string; label: string; tone: string }> = [
  { id: 'current',  label: 'Current',    tone: 'text-token-primary' },
  { id: 'd1_30',    label: '1–30 days',  tone: 'text-token-warning' },
  { id: 'd31_60',   label: '31–60 days', tone: 'text-token-warning' },
  { id: 'd61_90',   label: '61–90 days', tone: 'text-token-danger' },
  { id: 'd90_plus', label: '90+ days',   tone: 'text-token-danger' },
]

function AgingCard() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['invoice-aging'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('invoice_aging')
      if (error) throw error
      return (data ?? []) as Array<{ bucket: string; invoice_count: number; amount: number }>
    },
  })

  const by = new Map((data ?? []).map((r) => [r.bucket, r]))
  const total = (data ?? []).reduce((s, r) => s + Number(r.amount ?? 0), 0)
  // Nothing owed is worth saying plainly rather than showing five zeroes.
  if (!isLoading && total === 0) return null

  return (
    <Card className="mt-4 p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <p className="font-display text-[15px] font-bold text-token-primary">Who owes you, and for how long</p>
        <p className="text-[12.5px] text-token-secondary">{money(total)} outstanding</p>
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
        {BUCKETS.map((b) => {
          const row = by.get(b.id)
          const amount = Number(row?.amount ?? 0)
          const count = Number(row?.invoice_count ?? 0)
          const empty = amount === 0
          return (
            <button
              key={b.id}
              type="button"
              disabled={empty}
              onClick={() => navigate({ to: '/invoices', search: { aging: b.id } })}
              className={cn(
                'rounded-[12px] border border-token-subtle bg-token-surface px-4 py-3.5 text-left transition-colors',
                empty ? 'opacity-55' : 'hover:border-token-subtle hover:bg-canvas-subtle',
              )}
            >
              <span className="block text-[12px] font-semibold uppercase tracking-[0.06em] text-token-secondary">{b.label}</span>
              <span className={cn('mt-1.5 block font-display text-[21px] font-bold tabular-nums', empty ? 'text-token-muted' : b.tone)}>
                {isLoading ? '—' : money(amount)}
              </span>
              <span className="mt-0.5 block text-[12px] text-token-secondary">
                {isLoading ? ' ' : `${count} invoice${count === 1 ? '' : 's'}`}
              </span>
            </button>
          )
        })}
      </div>
    </Card>
  )
}

/* ─────────────────────────── Job run sheet ─────────────────────────── */
const UNASSIGNED = '__none__'
const startOfWeek = (iso: string) => isoToDate(startOfWeekIso(iso))
const runTime = (iso: string | null) => (iso ? phFormat(new Date(iso), 'time', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase() : '')
const longDate = (iso: string) => phFormat(phDay(iso), 'date', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })

interface RunJob {
  id: number; name: string | null; planned_start_date: string | null
  address: string | null; city: string | null; state: string | null; postal_code: string | null; line1: string | null; line2: string | null; manual_address: boolean | null
  contact: { first_name: string | null; last_name: string | null; organization: string | null; phone: string | null; mobile: string | null } | null
  task_field_value: Array<{ value_text: string | null; field: { name: string | null; type: string | null; row: number | null } | null }> | null
}
/**
 * Branching on `manual_address` was wrong: it decided WHERE to look rather than what
 * exists. Jobs imported from RouteMate Plus carry the street in `line1` but leave the
 * free-text `address` empty and `manual_address` false — so the non-manual branch fell
 * through to city/state/postcode and printed "Richlands Qld 4077" with no street number.
 * A run sheet without a street number cannot be driven to. Use whatever we actually have.
 */
const jobAddress = (j: RunJob) => {
  const street = [j.line1, j.line2].filter(Boolean).join(' ').trim()
  const region = [j.city, j.state, j.postal_code].filter(Boolean).join(' ').trim()
  if (street) return [street, region].filter(Boolean).join(', ')
  return (j.address || '').trim() || region
}
/**
 * The free-text fields, in form order. A customer records what the job actually IS here
 * ("3 ROOMS, HALL AND STAIRS - $271") because the old system had no quoting, so for them
 * this is the single most important line on the sheet. Field ids differ per tenant, so
 * select by field TYPE rather than hardcoding one.
 */
const jobNotes = (j: RunJob) => (j.task_field_value ?? [])
  .filter((v) => v.value_text?.trim() && (v.field?.type === 'paragraph' || v.field?.type === 'text'))
  .sort((a, b) => (a.field?.row ?? 0) - (b.field?.row ?? 0))
  .map((v) => v.value_text!.trim())
const cName = (c: RunJob['contact']) => (c ? [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.organization || '' : '')

function JobRunSheet() {
  const { members } = useTeam()
  const { user, role } = useAuth()
  const isAdmin = role === 'company-admin'
  const [from, setFrom] = useState(todayIso())
  const [to, setTo] = useState(todayIso())
  // Admins default to ALL technicians — a run sheet is usually printed for the whole crew, and an
  // owner opening it to their own (often empty) day reads as "there is no work on".
  // A non-admin only ever sees their own jobs, so it defaults to them.
  const [tech, setTech] = useState(role === 'company-admin' ? '' : (user?.id ?? ''))

  const preset = (kind: 'day' | 'week' | 'month') => {
    const now = new Date()
    if (kind === 'day') { setFrom(todayIso()); setTo(todayIso()) }
    else if (kind === 'week') { const s = startOfWeek(todayIso()); const e = new Date(s); e.setDate(s.getDate() + 6); setFrom(dayIso(s)); setTo(dayIso(e)) }
    else { setFrom(dayIso(new Date(now.getFullYear(), now.getMonth(), 1))); setTo(dayIso(new Date(now.getFullYear(), now.getMonth() + 1, 0))) }
  }

  const q = useQuery({
    queryKey: ['runsheet', from, to, tech],
    queryFn: async (): Promise<RunJob[]> => {
      let query = supabase.from('task')
        .select('id, name, planned_start_date, address, city, state, postal_code, line1, line2, manual_address, contact:contact(first_name, last_name, organization, phone, mobile), task_field_value(value_text, field:custom_form_field(name, type, row))')
        .is('deleted_at', null)
        // Spec 036. A draft is not a booking: an Android draft-identity bug left synced draft
        // twins beside real jobs, and this query printed both on a customer's run sheet (2026-08-10).
        // The Jobs list already excludes drafts; a crew cannot be sent to a job that was never
        // finished being entered.
        .eq('is_draft', false)
        // Local-day boundaries → UTC instants (jobs are stored in UTC; a Brisbane
        // morning maps to the previous UTC day, so naive UTC strings dropped them).
        .gte('planned_start_date', manilaWallToIso(from, '00:00:00')!)
        .lte('planned_start_date', manilaWallToIso(to, '23:59:59.999')!)
      if (tech === UNASSIGNED) query = query.is('user_id', null)
      else if (tech) query = query.eq('user_id', tech)
      const { data, error } = await query.order('planned_start_date', { ascending: true })
      if (error) throw error
      return (data ?? []) as unknown as RunJob[]
    },
  })

  const jobs = q.data ?? []
  const byDay = new Map<string, RunJob[]>()
  jobs.forEach((j) => { if (!j.planned_start_date) return; const k = manilaDayIso(j.planned_start_date); if (!byDay.has(k)) byDay.set(k, []); byDay.get(k)!.push(j) })
  const days = [...byDay.keys()].sort()

  return (
    <div>
      {/* Controls (hidden when printing) */}
      <Card className="mb-4 print:hidden">
        <div className="flex flex-wrap items-end gap-3 p-3.5">
          <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-[9px] border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong" /></label>
          <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-[9px] border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong" /></label>
          {isAdmin && (
            <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Technician</span>
              <Select value={tech} onChange={(e) => setTech(e.target.value)} className="h-9 w-[160px]"><option value="">All</option><option value={UNASSIGNED}>Unassigned</option>{members.map((m) => <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>)}</Select></label>
          )}
          <div className="flex items-center gap-1.5">
            {(['day', 'week', 'month'] as const).map((p) => <button key={p} onClick={() => preset(p)} className="rounded-full border border-token-subtle bg-token-surface px-3 py-1.5 text-[12.5px] font-semibold capitalize text-token-secondary hover:border-token-strong hover:text-token-primary">{p === 'day' ? 'Today' : `This ${p}`}</button>)}
          </div>
          <button className={btnSecondary + ' ml-auto'} onClick={() => window.print()}><Printer className="h-4 w-4" /> Print / PDF</button>
        </div>
      </Card>

      {/* The report */}
      <Card className="p-6 sm:p-8 print:border-0 print:shadow-none">
        <div className="mb-5 border-b border-token-subtle pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-token-muted">Job run sheet</p>
          <h1 className="font-display mt-1 text-[20px] font-bold tracking-[-0.01em] text-token-primary">{longDate(from)} – {longDate(to)}</h1>
          {tech && tech !== UNASSIGNED && <p className="mt-0.5 text-[13px] text-token-secondary">{[members.find((m) => m.id === tech)?.first_name, members.find((m) => m.id === tech)?.last_name].filter(Boolean).join(' ')}</p>}
          <p className="mt-0.5 text-[12.5px] text-token-secondary">{jobs.length} job{jobs.length === 1 ? '' : 's'}</p>
        </div>

        {q.isLoading ? <div className="py-6"><DetailSkeleton /></div>
          : jobs.length === 0 ? <p className="py-8 text-center text-[13px] text-token-secondary">No jobs scheduled in this range.</p>
          : days.map((k) => (
            <div key={k} className="mb-6 break-inside-avoid">
              {days.length > 1 && <p className="font-display mb-3 text-[14px] font-bold text-token-primary">{longDate(k)}</p>}
              <div className="space-y-4">
                {byDay.get(k)!.map((j) => {
                  const phone = j.contact?.phone || j.contact?.mobile || ''
                  return (
                    <div key={j.id} className="break-inside-avoid">
                      <div className="flex items-baseline gap-3">
                        <span className="font-display w-16 flex-none text-[14px] font-bold tabular-nums text-token-primary">{runTime(j.planned_start_date)}</span>
                        <span className="text-[14px] font-bold text-token-primary">{cName(j.contact) || '—'}</span>
                        <span className="text-[14px] text-token-primary">{j.name || ''}</span>
                      </div>
                      <div className="pl-[76px] text-[13px] text-token-secondary">{phone || '—'}</div>
                      <div className="pl-[76px] text-[13px] text-token-secondary">{jobAddress(j) || '—'}</div>
                      {jobNotes(j).map((n, i) => (
                        <div key={i} className="whitespace-pre-wrap pl-[76px] text-[13px] text-token-primary">{n}</div>
                      ))}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
      </Card>
    </div>
  )
}

function Donut({ pct }: { pct: number }) {
  const r = 52, c = 2 * Math.PI * r
  const dash = (pct / 100) * c
  return (
    <div className="relative h-[132px] w-[132px] flex-none">
      <svg viewBox="0 0 132 132" className="h-full w-full -rotate-90">
        <circle cx="66" cy="66" r={r} fill="none" stroke="#eef0f3" strokeWidth="16" />
        <circle cx="66" cy="66" r={r} fill="none" stroke="var(--accent)" strokeWidth="16" strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display text-[24px] font-bold tabular-nums text-token-primary">{pct}%</span>
      </div>
    </div>
  )
}

/**
 * Quoted vs actual, per finished job (spec 032, supersedes the quote-cost-only version that lived
 * here). Rows come from job_costing_rows() — the same maths as the job page's profit card, so the
 * two can never disagree. Where nothing is logged the quoted cost stands in, counted and labelled,
 * because a row that silently swaps its basis is how a 90%-estimated report masquerades as truth.
 */
function ProfitReport() {
  const [days, setDays] = useState(90)
  const { company, save } = useCompany()
  const target = company?.target_margin_pct ?? null
  const to = todayIso()
  const from = addDaysIso(to, -days)
  const rowsQ = useCostingRows(from, to)
  const rows = rowsQ.data ?? []

  const enriched = rows.map((r) => {
    const hasActuals = r.labour_cost > 0 || r.material_cost > 0
    const cost = hasActuals ? r.labour_cost + r.material_cost : r.quoted_cost
    const revenue = r.invoiced
    const quotedPct = r.quoted_subtotal > 0 ? Math.round(((r.quoted_subtotal - r.quoted_cost) / r.quoted_subtotal) * 100) : null
    const actualPct = revenue > 0 ? Math.round(((revenue - cost) / revenue) * 100) : null
    return { ...r, hasActuals, cost, revenue, quotedPct, actualPct }
  }).filter((r) => r.revenue > 0 || r.quoted_subtotal > 0)

  const invoiced = enriched.reduce((s, r) => s + r.revenue, 0)
  const totalCost = enriched.reduce((s, r) => s + r.cost, 0)
  const measured = enriched.filter((r) => r.hasActuals).length
  const unrated = rows.reduce((s, r) => s + r.unrated_minutes, 0)

  // The leak-finder: clients whose delivered margin runs under their quoted margin.
  const byClient = new Map<string, { revenue: number; cost: number; qSub: number; qCost: number; n: number }>()
  for (const r of enriched) {
    const k = r.client ?? '—'
    const e = byClient.get(k) ?? { revenue: 0, cost: 0, qSub: 0, qCost: 0, n: 0 }
    e.revenue += r.revenue; e.cost += r.cost; e.qSub += r.quoted_subtotal; e.qCost += r.quoted_cost; e.n++
    byClient.set(k, e)
  }
  const leaks = [...byClient.entries()].map(([client, e]) => {
    const aPct = e.revenue > 0 ? Math.round(((e.revenue - e.cost) / e.revenue) * 100) : null
    const qPct = e.qSub > 0 ? Math.round(((e.qSub - e.qCost) / e.qSub) * 100) : null
    return { client, ...e, aPct, qPct, gap: aPct != null && qPct != null ? aPct - qPct : null }
  }).filter((l) => l.gap != null && l.gap < -2).sort((a, b) => (a.gap ?? 0) - (b.gap ?? 0)).slice(0, 5)

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-1.5">
          {[30, 90, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={cn('rounded-full px-3 py-1.5 text-[12.5px] font-medium', days === d ? 'bg-brand text-brand-fg' : 'bg-token-surface text-token-secondary border border-token-subtle')}>
              {d === 365 ? '12 months' : `${d} days`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-[12.5px] text-token-secondary">
            Target margin
            <input
              defaultValue={target ?? ''}
              onBlur={async (e) => {
                const v = e.target.value.trim()
                const n = v === '' ? null : Math.round(Number(v))
                if (v !== '' && (!Number.isFinite(n!) || n! < 1 || n! > 95)) return toast.error('Target must be 1–95%')
                if (n === target) return
                try { await save.mutateAsync({ target_margin_pct: n }); toast.success(n == null ? 'Target cleared' : `Target set to ${n}%`) }
                catch (err) { toast.error(err instanceof Error ? err.message : 'Failed') }
              }}
              placeholder="—" inputMode="numeric"
              className="w-14 rounded-[8px] border border-token-subtle px-2 py-1 text-right text-[12.5px]"
            />%
          </label>
          <p className="text-[12px] text-token-muted">{measured} of {enriched.length} jobs measured · rest estimated · ex VAT</p>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label={`Invoiced · ${days}d`} value={money(invoiced)} delta={<span className="text-token-secondary">{enriched.length} finished job{enriched.length === 1 ? '' : 's'}</span>} />
        <KpiCard label="Cost" value={money(totalCost)} delta={<span className="text-token-secondary">labour + materials, quoted where unlogged</span>} />
        <KpiCard label="Gross margin" value={money(invoiced - totalCost)} delta={<span className={invoiced - totalCost < 0 ? 'text-token-danger' : 'text-token-success'}>{invoiced > 0 ? Math.round(((invoiced - totalCost) / invoiced) * 100) : 0}% of invoiced</span>} />
        <KpiCard label="Unrated hours" value={(unrated / 60).toFixed(1)} tone={unrated > 0 ? 'orange' : 'ink'} delta={<span className="text-token-warning">logged time with no cost rate set</span>} />
      </div>

      {leaks.length > 0 && (
        <Card className="mb-4 p-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Delivering under quote</p>
          {leaks.map((l) => (
            <p key={l.client} className="py-1 text-[13px] text-token-primary">
              <span className="font-semibold">{l.client}</span>
              <span className="text-token-secondary"> · {l.n} job{l.n === 1 ? '' : 's'} · quoted {l.qPct}%, delivered </span>
              <span className="font-semibold text-token-danger">{l.aPct}%</span>
            </p>
          ))}
        </Card>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead><tr className="border-b border-token-subtle text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">
              <th className="px-4 py-3">Job</th><th className="px-4 py-3">Client</th>
              <th className="px-4 py-3 text-right">Invoiced</th><th className="px-4 py-3 text-right">Cost</th>
              <th className="px-4 py-3 text-right">Quoted %</th><th className="px-4 py-3 text-right">Actual %</th>
              <th className="px-4 py-3">Basis</th>
            </tr></thead>
            <tbody>
              {enriched.map((r) => (
                <tr key={r.task_id} className="border-b border-token-subtle">
                  <td className="px-4 py-2.5 font-semibold text-token-primary">
                    <Link to="/jobs/$id" params={{ id: String(r.task_id) }} className="hover:underline">{r.name || `#J-${r.task_id}`}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-token-secondary">{r.client ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{money(r.revenue)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-token-secondary">{money(r.cost)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-token-secondary">{r.quotedPct != null ? r.quotedPct + '%' : '—'}</td>
                  <td className={"px-4 py-2.5 text-right font-semibold tabular-nums " + (r.actualPct != null && r.quotedPct != null && r.actualPct < r.quotedPct - 2 ? 'text-token-danger' : 'text-token-primary')}>{r.actualPct != null ? r.actualPct + '%' : '—'}</td>
                  <td className="px-4 py-2.5 text-[12px] text-token-muted">{r.hasActuals ? 'measured' : 'estimated'}</td>
                </tr>
              ))}
              {enriched.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-token-secondary">No finished jobs in this period.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-[11.5px] text-token-secondary">
          "Measured" jobs have logged hours or materials; "estimated" fall back to quote-line costs. Log time at
          completion and set cost rates in Settings → Team to move jobs into the measured column.
        </p>
      </Card>
    </div>
  )
}

/* ─────────────────────────── Marketing sources ─────────────────────────── */
/**
 * Where the work came from, counted by job (customer request).
 *
 * The answer lives in a tenant's OWN dropdown field — a customer added "How did you hear about us?" to
 * her Job/Task form herself — so nothing here may hardcode a field id or an option list. The picker
 * offers the dropdown fields on the PRIMARY form, which is where every job actually sits.
 *
 * COUNTS ONLY. No money and no percentages: the owner asked for a plain breakdown, and a share of
 * total invites "share of what?" on a period where some jobs carry no answer at all. The bar shows
 * relative size; it is not a figure to read off.
 *
 * Unanswered jobs are counted and shown. A breakdown that silently drops them reads as the whole
 * picture, and a marketing decision made on a quarter of the jobs is worse than no decision.
 *
 * This tab reads the tables directly under RLS rather than through `report_sections`. It is a page
 * you look at and print, not a section that can be emailed or shared on a token, so the one-RPC
 * rule that binds the Custom tab's sections does not apply here.
 */
interface SourceField { id: string; name: string; options: string[] | null }

function MarketingReport() {
  const { companyId } = useAuth()
  const [from, setFrom] = useState(addDaysIso(todayIso(), -89))
  const [to, setTo] = useState(todayIso())
  const [pickedId, setPickedId] = useState('')

  const preset = (kind: 'month' | 'lastMonth' | 'year') => {
    const now = new Date()
    if (kind === 'month') { setFrom(dayIso(new Date(now.getFullYear(), now.getMonth(), 1))); setTo(todayIso()) }
    else if (kind === 'lastMonth') { setFrom(dayIso(new Date(now.getFullYear(), now.getMonth() - 1, 1))); setTo(dayIso(new Date(now.getFullYear(), now.getMonth(), 0))) }
    else { setFrom(dayIso(new Date(now.getFullYear(), 0, 1))); setTo(todayIso()) }
  }

  const fieldsQ = useQuery({
    queryKey: ['marketing-source-fields', companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<SourceField[]> => {
      const { data, error } = await supabase
        .from('custom_form_field')
        .select('id, name, options, hidden, row, custom_form!inner(company_id, is_primary)')
        .eq('custom_form.company_id', companyId!)
        .eq('custom_form.is_primary', true)
        .eq('type', 'dropdown')
        .is('deleted_at', null)
        .order('row', { ascending: true })
      if (error) throw error
      return ((data ?? []) as Array<{ id: string; name: string; options: string[] | null; hidden: boolean | null }>)
        .filter((f) => !f.hidden)
        .map((f) => ({ id: f.id, name: f.name, options: f.options }))
    },
  })
  const fields = fieldsQ.data ?? []
  // One dropdown needs no choosing. More than one, and the first is a guess until she picks.
  const field = fields.find((f) => f.id === pickedId) ?? fields[0]

  const countsQ = useQuery({
    queryKey: ['marketing-sources', field?.id, from, to],
    enabled: !!field,
    queryFn: async (): Promise<{ jobs: number; answered: number; tally: Map<string, number> }> => {
      // Local-day boundaries → UTC instants, the same conversion the run sheet uses. Jobs are
      // stored in UTC and a Brisbane morning belongs to the previous UTC day.
      const fromIso = manilaWallToIso(from, '00:00:00')!
      const toIso = manilaWallToIso(to, '23:59:59.999')!
      // Two queries on purpose. The first counts every job in the period, so "not answered" is a
      // real number rather than the absence of a row. A job with no planned date is in neither
      // query, because it cannot be placed in a period.
      const [jobs, answers] = await Promise.all([
        supabase.from('task').select('id', { count: 'exact', head: true })
          .is('deleted_at', null).eq('is_draft', false)
          .gte('planned_start_date', fromIso).lte('planned_start_date', toIso),
        supabase.from('task_field_value')
          .select('value_text, task:task!inner(id)')
          .eq('field_id', field!.id)
          .is('task.deleted_at', null).eq('task.is_draft', false)
          .gte('task.planned_start_date', fromIso).lte('task.planned_start_date', toIso),
      ])
      if (jobs.error) throw jobs.error
      if (answers.error) throw answers.error
      // Every figure on this page is read against the job count, "not answered" most of all. A
      // null count would print "0 jobs booked" next to a column of real answers, which reads as
      // "we have no work" rather than "this number did not load". Fail loudly instead.
      if (jobs.count == null) throw new Error('the job count did not come back')
      const tally = new Map<string, number>()
      let answered = 0
      for (const r of (answers.data ?? []) as Array<{ value_text: string | null }>) {
        // The row can exist with an empty value: the question was on the job and nobody picked.
        const v = (r.value_text ?? '').trim()
        if (!v) continue
        tally.set(v, (tally.get(v) ?? 0) + 1)
        answered++
      }
      return { jobs: jobs.count ?? 0, answered, tally }
    },
  })

  const jobs = countsQ.data?.jobs ?? 0
  const answered = countsQ.data?.answered ?? 0
  const { rows, blank, widest } = sourceBreakdown({
    options: field?.options,
    tally: countsQ.data?.tally ?? new Map<string, number>(),
    jobs,
    answered,
  })

  if (fieldsQ.isLoading) return <Card className="p-6"><DetailSkeleton /></Card>

  if (!field) {
    return (
      <Card className="p-6">
        <p className="font-display text-[15px] font-bold text-token-primary">No question to count yet</p>
        <p className="mt-2 max-w-xl text-[13px] text-token-secondary">
          This report counts the answers to a dropdown question on your job form. Add one first. Open Forms, open
          your job form, click Edit form, add a Dropdown field such as "How did you hear about us?", and type your
          choices. Every new job then asks the question, and the answers show up here.
        </p>
        <Link to="/forms" className={btnSecondary + ' mt-4 inline-flex'}>Go to Forms</Link>
      </Card>
    )
  }

  return (
    <div>
      {/* Controls (hidden when printing) */}
      <Card className="mb-4 print:hidden">
        <div className="flex flex-wrap items-end gap-3 p-3.5">
          <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-[9px] border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong" /></label>
          <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-[9px] border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong" /></label>
          {fields.length > 1 && (
            <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Question</span>
              <Select value={field.id} onChange={(e) => setPickedId(e.target.value)} className="h-9 w-[220px]">
                {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </Select></label>
          )}
          <div className="flex items-center gap-1.5">
            <button onClick={() => preset('month')} className="rounded-full border border-token-subtle bg-token-surface px-3 py-1.5 text-[12.5px] font-semibold text-token-secondary hover:border-token-strong hover:text-token-primary">This month</button>
            <button onClick={() => preset('lastMonth')} className="rounded-full border border-token-subtle bg-token-surface px-3 py-1.5 text-[12.5px] font-semibold text-token-secondary hover:border-token-strong hover:text-token-primary">Last month</button>
            <button onClick={() => preset('year')} className="rounded-full border border-token-subtle bg-token-surface px-3 py-1.5 text-[12.5px] font-semibold text-token-secondary hover:border-token-strong hover:text-token-primary">This year</button>
          </div>
          <button className={btnSecondary + ' ml-auto'} onClick={() => window.print()}><Printer className="h-4 w-4" /> Print / PDF</button>
        </div>
      </Card>

      {/* The report */}
      <Card className="p-6 sm:p-8 print:border-0 print:shadow-none">
        <div className="mb-5 border-b border-token-subtle pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-token-muted">Where the work came from</p>
          <h1 className="font-display mt-1 text-[20px] font-bold tracking-[-0.01em] text-token-primary">{longDate(from)} – {longDate(to)}</h1>
          <p className="mt-0.5 text-[13px] text-token-secondary">{field.name}</p>
          <p className="mt-0.5 text-[12.5px] text-token-secondary">
            {jobs} job{jobs === 1 ? '' : 's'} booked · {answered} answered{blank > 0 ? ` · ${blank} not answered` : ''}
          </p>
        </div>

        {countsQ.isLoading ? <div className="py-6"><DetailSkeleton /></div>
          : countsQ.isError ? <p className="py-8 text-center text-[13px] text-token-danger">That did not load. Please try again.</p>
          : jobs === 0 && answered === 0 ? <p className="py-8 text-center text-[13px] text-token-secondary">No jobs booked in this range.</p>
          : (
            <>
              <div className="space-y-3">
                {rows.map((r) => (
                  <div key={r.label} className="break-inside-avoid">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[13.5px] text-token-primary">
                        {r.label}
                        {r.retired && <span className="ml-2 text-[11.5px] text-token-muted">no longer a choice</span>}
                      </span>
                      <span className="font-display text-[15px] font-bold tabular-nums text-token-primary">{r.count}</span>
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-canvas-subtle print:border print:border-token-subtle">
                      <div className="h-full rounded-full" style={{ width: `${Math.round((r.count / widest) * 100)}%`, background: 'var(--accent)' }} />
                    </div>
                  </div>
                ))}
              </div>

              {blank > 0 && (
                <div className="mt-5 flex items-baseline justify-between gap-3 border-t border-token-subtle pt-3.5">
                  <span className="text-[13.5px] text-token-secondary">Not answered</span>
                  <span className="font-display text-[15px] font-bold tabular-nums text-token-secondary">{blank}</span>
                </div>
              )}

              <p className="mt-5 text-[11.5px] text-token-secondary">
                Counted by job, on the date the job is booked for. A job with no date is not counted.
              </p>
            </>
          )}
      </Card>
    </div>
  )
}

function TimesheetReport() {
  const { members } = useTeam()
  const { role } = useAuth()
  const isAdmin = role === 'company-admin'
  const monday = (() => { const d = new Date(); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); return d })()
  const [from, setFrom] = useState(dayIso(monday))
  const [to, setTo] = useState(todayIso())
  const q = useTimesheet(from, to)
  const entries = q.data ?? []
  const jobIds = [...new Set(entries.map((e) => e.task_id).filter(Boolean))] as number[]
  const jobs = useQuery({
    queryKey: ['ts-jobs', jobIds.join(',')],
    queryFn: async () => {
      if (!jobIds.length) return {} as Record<number, string>
      const { data } = await supabase.from('task').select('id, name').in('id', jobIds)
      return Object.fromEntries((data ?? []).map((r) => [r.id as number, (r.name as string) ?? 'Job']))
    },
    enabled: jobIds.length > 0,
  })
  const name = (uid: string) => { const m = members.find((x) => x.id === uid); return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Tech' : 'Tech' }
  const rate = (uid: string) => Number(members.find((x) => x.id === uid)?.hourly_cost ?? 0)
  const byTech = new Map<string, TimeEntry[]>()
  entries.forEach((e) => { if (!byTech.has(e.user_id)) byTech.set(e.user_id, []); byTech.get(e.user_id)!.push(e) })
  return (
    <div>
      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3 p-3.5">
          <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-[9px] border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong" /></label>
          <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-[9px] border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong" /></label>
          <p className="pb-2 text-[12px] text-token-secondary">Clock on/off from any job (web or mobile). {isAdmin ? 'Set hourly cost rates under Settings → Team to see labour cost.' : 'Showing your hours.'}</p>
          {isAdmin && FEATURES.payrollExport && <PayrollExportButton from={from} to={to} />}
        </div>
      </Card>
      {[...byTech.entries()].map(([uid, list]) => {
        const total = list.reduce((s, e) => s + entryMinutes(e), 0)
        const cost = rate(uid) > 0 ? (total / 60) * rate(uid) : null
        return (
          <Card key={uid} className="mb-4">
            <div className="flex items-center justify-between border-b border-token-subtle px-4 py-3">
              <p className="text-[14px] font-bold text-token-primary">{name(uid)}</p>
              <p className="text-[13px] font-bold tabular-nums text-token-primary">{fmtHours(total)}{cost !== null && isAdmin ? <span className="ml-2 text-token-success">{money(cost)}</span> : null}</p>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-left text-[13px]">
              <tbody>
                {list.map((e) => (
                  <tr key={e.id} className="border-b border-token-subtle">
                    <td className="px-4 py-2 text-token-secondary">{phFormat(new Date(e.started_at), 'date', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                    <td className="px-4 py-2 text-token-secondary">{e.task_id ? (jobs.data?.[e.task_id] ?? `Job #${e.task_id}`) : '—'}</td>
                    <td className="px-4 py-2 tabular-nums text-token-secondary">{phFormat(new Date(e.started_at), 'time', { hour: '2-digit', minute: '2-digit', hour12: false })}–{e.ended_at ? phFormat(new Date(e.ended_at), 'time', { hour: '2-digit', minute: '2-digit', hour12: false }) : 'now'}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums">{e.ended_at ? fmtHours(entryMinutes(e)) : <span className="text-token-success">running</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>
        )
      })}
      {entries.length === 0 && !q.isLoading && <Card><p className="px-4 py-10 text-center text-[13px] text-token-secondary">No time tracked in this range yet — clock on from any job page.</p></Card>}
    </div>
  )
}

/**
 * Spec 023 · payroll export. Downloads the period as a CSV for Employment Hero / KeyPay.
 *
 * The rows are formatted BY THE DATABASE in the company's timezone — a 7:00am Sydney start must not
 * be rendered from UTC, or it lands on the previous day and in the previous pay week.
 */
function PayrollExportButton({ from, to }: { from: string; to: string }) {
  const [busy, setBusy] = useState(false)
  const [warn, setWarn] = useState<string[] | null>(null)

  const run = async () => {
    setBusy(true); setWarn(null)
    try {
      const res = await fetchPayrollExport(from, to)
      if (!res.rows.length) { toast.error('No finished timesheets in that period'); return }
      // Warn but still export: a missing External ID is fixable in Settings, and blocking the
      // download entirely would be worse than letting them see what is in the file.
      if (res.missing_payroll_id.length) setWarn(res.missing_payroll_id)
      downloadCsv(`timesheets-${from}-to-${to}.csv`, toCsv(res.rows))
      toast.success(`${res.rows.length} timesheet ${res.rows.length === 1 ? 'row' : 'rows'} exported`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not build the export')
    } finally { setBusy(false) }
  }

  return (
    <div className="ml-auto flex flex-col items-end gap-1.5 pb-1">
      <button className={btnSecondary} onClick={run} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Payroll CSV
      </button>
      {warn && (
        <p className="max-w-[300px] text-right text-[11.5px] leading-snug text-token-warning">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          No payroll ID for {warn.join(', ')}. Add it under Settings &rarr; Team so payroll can match
          them, or the import won&rsquo;t know who these hours belong to.
        </p>
      )}
    </div>
  )
}


/**
 * 050 T012 — saved report templates.
 *
 * A fifth tab rather than a new page: somebody looking for a report looks at Reports, and the four
 * fixed reports and the ones they built themselves are the same errand.
 */
function CustomReports() {
  const nav = useNavigate()
  const { data: templates, isLoading } = useReportTemplates()
  const { remove } = useReportMutations()
  const [confirm, confirmDialog] = useConfirm()

  return (
    <>
      <Card>
        <CardHeader
          title="Your reports"
          count={templates?.length}
          action={
            <button type="button" className={btnPrimary} onClick={() => nav({ to: '/reports/$id', params: { id: 'new' } })}>
              <Plus className="h-4 w-4" /> New report
            </button>
          }
        />
        {isLoading ? (
          <SkeletonRows count={3} />
        ) : !templates?.length ? (
          <EmptyState
            label="No saved reports yet. Build one from the sections you actually want, then share it as a link or have it emailed every Monday."
            cta="New report"
            onCta={() => nav({ to: '/reports/$id', params: { id: 'new' } })}
          />
        ) : (
          <ul className="divide-y divide-token">
            {templates.map((t) => (
              <li
                key={t.id}
                className="group flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-token-hover"
                onClick={() => nav({ to: '/reports/$id', params: { id: t.id } })}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-token-primary">{t.name}</p>
                  <p className="truncate text-[12px] text-token-secondary">
                    {rangeLabel(t.config.range_kind)} · {t.config.sections.length} section{t.config.sections.length === 1 ? '' : 's'}
                    {t.config.user_ids.length > 0 && ` · ${t.config.user_ids.length} person filter`}
                  </p>
                </div>
                <div className={`flex items-center gap-1 ${hoverActions}`} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button" className={btnGhost} aria-label={`Delete ${t.name}`}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete "${t.name}"?`,
                        body: 'Links already shared keep working until they expire — they carry their own copy of the period.',
                        confirmLabel: 'Delete', tone: 'danger',
                      })
                      if (ok) remove.mutate(t.id, { onSuccess: () => toast.success('Deleted') })
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {confirmDialog}
    </>
  )
}
